import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Plus, Pencil, Trash2, GripVertical,
  Folder, ExternalLink, Save, X, ChevronDown, ChevronRight,
} from "lucide-react";

const API = `${getApiUrl()}/api`;

export default function MasterDatabaseLinks() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group form
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", order: 0 });

  // Link form
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkTargetGroupId, setLinkTargetGroupId] = useState(null);
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "", icon: "", order: 0 });

  // Drag and drop state
  const [draggedLink, setDraggedLink] = useState(null);
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef({});

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/links/groups`);
      setGroups(res.data);
      // Auto-expand all groups on first load
      if (Object.keys(expandedGroups).length === 0) {
        const expanded = {};
        res.data.forEach(g => { expanded[g.id] = true; });
        setExpandedGroups(expanded);
      }
    } catch (err) {
      console.error("Failed to fetch link groups:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // --- Group CRUD ---
  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: "", description: "", order: groups.length });
    setShowGroupForm(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, description: group.description || "", order: group.order || 0 });
    setShowGroupForm(true);
  };

  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error("Group name is required"); return; }
    try {
      if (editingGroup) {
        await axios.put(`${API}/links/groups/${editingGroup.id}`, groupForm);
        toast.success("Group updated");
      } else {
        await axios.post(`${API}/links/groups`, groupForm);
        toast.success("Group created");
      }
      setShowGroupForm(false);
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save group");
    }
  };

  const deleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Delete group "${groupName}" and all its links?`)) return;
    try {
      await axios.delete(`${API}/links/groups/${groupId}`);
      toast.success("Group deleted");
      fetchGroups();
    } catch (err) {
      toast.error("Failed to delete group");
    }
  };

  // --- Link CRUD ---
  const openNewLink = (groupId) => {
    setEditingLink(null);
    setLinkTargetGroupId(groupId);
    setLinkForm({ title: "", url: "", description: "", icon: "", order: 0 });
    setShowLinkForm(true);
  };

  const openEditLink = (link) => {
    setEditingLink(link);
    setLinkTargetGroupId(link.groupId);
    setLinkForm({
      title: link.title,
      url: link.url,
      description: link.description || "",
      icon: link.icon || "",
      order: link.order || 0,
    });
    setShowLinkForm(true);
  };

  const saveLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    try {
      if (editingLink) {
        await axios.put(`${API}/links/${editingLink.id}`, { ...linkForm, groupId: linkTargetGroupId });
        toast.success("Link updated");
      } else {
        await axios.post(`${API}/links`, { ...linkForm, groupId: linkTargetGroupId });
        toast.success("Link created");
      }
      setShowLinkForm(false);
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save link");
    }
  };

  const deleteLink = async (linkId, linkTitle) => {
    if (!window.confirm(`Delete link "${linkTitle}"?`)) return;
    try {
      await axios.delete(`${API}/links/${linkId}`);
      toast.success("Link deleted");
      fetchGroups();
    } catch (err) {
      toast.error("Failed to delete link");
    }
  };

  const toggleGroup = (id) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // --- Drag & Drop ---
  const handleDragStart = (e, link, sourceGroupId) => {
    setDraggedLink({ ...link, sourceGroupId });
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", link.id);
    // Expand all groups so user can see where to drop
    const expanded = {};
    groups.forEach(g => { expanded[g.id] = true; });
    setExpandedGroups(expanded);
  };

  const handleDragEnd = () => {
    setDraggedLink(null);
    setDragOverGroupId(null);
    setIsDragging(false);
    dragCounterRef.current = {};
  };

  const handleDragEnterGroup = (e, groupId) => {
    e.preventDefault();
    dragCounterRef.current[groupId] = (dragCounterRef.current[groupId] || 0) + 1;
    setDragOverGroupId(groupId);
  };

  const handleDragLeaveGroup = (e, groupId) => {
    dragCounterRef.current[groupId] = (dragCounterRef.current[groupId] || 0) - 1;
    if (dragCounterRef.current[groupId] <= 0) {
      dragCounterRef.current[groupId] = 0;
      if (dragOverGroupId === groupId) {
        setDragOverGroupId(null);
      }
    }
  };

  const handleDragOverGroup = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropOnGroup = async (e, targetGroupId) => {
    e.preventDefault();
    setDragOverGroupId(null);
    dragCounterRef.current = {};
    
    if (!draggedLink || draggedLink.sourceGroupId === targetGroupId) {
      setDraggedLink(null);
      setIsDragging(false);
      return;
    }

    const linkToMove = draggedLink;
    setDraggedLink(null);
    setIsDragging(false);

    // Optimistic update
    setGroups(prev => prev.map(g => {
      if (g.id === linkToMove.sourceGroupId) {
        return { ...g, links: (g.links || []).filter(l => l.id !== linkToMove.id) };
      }
      if (g.id === targetGroupId) {
        return { ...g, links: [...(g.links || []), { ...linkToMove, groupId: targetGroupId }] };
      }
      return g;
    }));

    try {
      await axios.put(`${API}/links/${linkToMove.id}`, { groupId: targetGroupId });
      toast.success(`Moved "${linkToMove.title}" to new group`);
    } catch (err) {
      toast.error("Failed to move link");
      fetchGroups(); // Revert on failure
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="mdb-links-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/master-database")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-white font-[Unbounded]">Manage Links</h1>
          <p className="text-zinc-400 text-sm">Create and organize link groups for users</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mb-6">
        <Button onClick={openNewGroup} className="bg-purple-600 hover:bg-purple-700" data-testid="new-group-btn">
          <Plus className="w-4 h-4 mr-1" /> New Group
        </Button>
      </div>

      {/* Group Form Modal */}
      {showGroupForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowGroupForm(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="group-form-modal">
            <h3 className="text-lg font-bold text-white mb-4">{editingGroup ? "Edit Group" : "New Group"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Name *</label>
                <Input
                  value={groupForm.name}
                  onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="e.g. Achievement Tracking Sites"
                  data-testid="group-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Description</label>
                <Input
                  value={groupForm.description}
                  onChange={e => setGroupForm({ ...groupForm, description: e.target.value })}
                  placeholder="Optional description"
                  data-testid="group-desc-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Sort Order</label>
                <Input
                  type="number"
                  value={groupForm.order}
                  onChange={e => setGroupForm({ ...groupForm, order: parseInt(e.target.value) || 0 })}
                  data-testid="group-order-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setShowGroupForm(false)} data-testid="group-cancel-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button onClick={saveGroup} className="bg-purple-600 hover:bg-purple-700" data-testid="group-save-btn">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Link Form Modal */}
      {showLinkForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkForm(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="link-form-modal">
            <h3 className="text-lg font-bold text-white mb-4">{editingLink ? "Edit Link" : "New Link"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Title *</label>
                <Input
                  value={linkForm.title}
                  onChange={e => setLinkForm({ ...linkForm, title: e.target.value })}
                  placeholder="e.g. TrueAchievements"
                  data-testid="link-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">URL *</label>
                <Input
                  value={linkForm.url}
                  onChange={e => setLinkForm({ ...linkForm, url: e.target.value })}
                  placeholder="https://..."
                  data-testid="link-url-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Description</label>
                <Input
                  value={linkForm.description}
                  onChange={e => setLinkForm({ ...linkForm, description: e.target.value })}
                  placeholder="Brief description"
                  data-testid="link-desc-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Icon URL</label>
                <Input
                  value={linkForm.icon}
                  onChange={e => setLinkForm({ ...linkForm, icon: e.target.value })}
                  placeholder="https://example.com/favicon.ico"
                  data-testid="link-icon-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Sort Order</label>
                <Input
                  type="number"
                  value={linkForm.order}
                  onChange={e => setLinkForm({ ...linkForm, order: parseInt(e.target.value) || 0 })}
                  data-testid="link-order-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setShowLinkForm(false)} data-testid="link-cancel-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button onClick={saveLink} className="bg-purple-600 hover:bg-purple-700" data-testid="link-save-btn">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Groups List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Folder className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No link groups yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(group => (
            <div
              key={group.id}
              className={`bg-zinc-900/60 border rounded-lg overflow-hidden transition-all duration-200 ${
                isDragging && dragOverGroupId === group.id && draggedLink?.sourceGroupId !== group.id
                  ? "border-purple-500 ring-2 ring-purple-500/30 bg-purple-500/5"
                  : isDragging && draggedLink?.sourceGroupId !== group.id
                  ? "border-zinc-700 border-dashed"
                  : "border-zinc-800"
              }`}
              onDragEnter={(e) => handleDragEnterGroup(e, group.id)}
              onDragLeave={(e) => handleDragLeaveGroup(e, group.id)}
              onDragOver={handleDragOverGroup}
              onDrop={(e) => handleDropOnGroup(e, group.id)}
              data-testid={`mdb-group-${group.id}`}
            >
              {/* Group Header */}
              <div className={`flex items-center gap-2 px-4 py-3 transition-colors ${
                isDragging && dragOverGroupId === group.id && draggedLink?.sourceGroupId !== group.id
                  ? "bg-purple-500/10"
                  : "bg-zinc-900/80"
              }`}>
                <button onClick={() => toggleGroup(group.id)} className="text-zinc-400 hover:text-white">
                  {expandedGroups[group.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <Folder className="w-4 h-4 text-purple-400" />
                <span className="text-white font-semibold flex-1">{group.name}</span>
                <span className="text-zinc-500 text-xs mr-2">{group.links?.length || 0} links</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditGroup(group)} data-testid={`edit-group-${group.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => deleteGroup(group.id, group.name)} data-testid={`delete-group-${group.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>

              {/* Group Content (links) */}
              {expandedGroups[group.id] && (
                <div className="px-4 py-2 border-t border-zinc-800/50">
                  {group.description && (
                    <p className="text-zinc-500 text-xs mb-2">{group.description}</p>
                  )}
                  {(!group.links || group.links.length === 0) ? (
                    <p className="text-zinc-600 text-sm py-3 text-center">No links in this group</p>
                  ) : (
                    <div className="space-y-1">
                      {group.links.map(link => (
                        <div
                          key={link.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, link, group.id)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-3 py-2 px-2 rounded hover:bg-zinc-800/50 group transition-all ${
                            draggedLink?.id === link.id ? "opacity-30 scale-95" : ""
                          }`}
                          data-testid={`mdb-link-${link.id}`}
                        >
                          <GripVertical className="w-4 h-4 text-zinc-700 flex-shrink-0 cursor-grab active:cursor-grabbing hover:text-zinc-400 transition-colors" />
                          {link.icon ? (
                            <img src={link.icon} alt="" className="w-5 h-5 rounded flex-shrink-0 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <ExternalLink className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-sm">{link.title}</span>
                            <span className="text-zinc-600 text-xs ml-2 truncate">{link.url}</span>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={() => openEditLink(link)} data-testid={`edit-link-${link.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300" onClick={() => deleteLink(link.id, link.title)} data-testid={`delete-link-${link.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 mb-1 text-purple-400 hover:text-purple-300"
                    onClick={() => openNewLink(group.id)}
                    data-testid={`add-link-to-${group.id}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Link
                  </Button>
                  {isDragging && dragOverGroupId === group.id && draggedLink?.sourceGroupId !== group.id && (
                    <div className="mx-2 mb-2 py-2 border-2 border-dashed border-purple-500/50 rounded-lg text-center text-purple-400 text-xs animate-pulse" data-testid="drop-indicator">
                      Drop here to move link
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
